import { Button } from "components/button";
import { StyledCalendar } from "components/StyledCalendar";
import { Text } from "components/text";
import { startOfDay } from "date-fns";
import { SearchFilterComparisonOperator } from "hooks/search/types";
import { FocusEvent, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "utils/cn";
import {
  buildSalesDataPresetRange,
  emptySalesDataRange,
  formatSalesDataInputDate,
  getActiveSalesDataPresetId,
  hasValidSalesDataMetricValue,
  parseSalesDataRange,
  type SalesDataPresetId,
  type SalesDataMetricKey,
} from "hooks/search/salesDataFilterUtils";
import { validateCompleteDateRange } from "hooks/search/dateRangeValidation";
import { DateInput } from "../dateFilter/DateInput";
import { SalesDataMetricRow } from "./SalesDataMetricRow";

const DATE_RANGE_ERROR_ID = "sales-data-date-range-error";
// Same overlay layer as SelectContent/PopoverContent, above the sticky header.
const CALENDAR_OVERLAY_CLASS_NAME =
  "absolute top-full z-[80] mt-2 rounded-[8px] border bg-white p-2 shadow-md";

const defaultSalesDataFilter = {
  range: emptySalesDataRange,
  totalUnits: {
    comparisonOperator: SearchFilterComparisonOperator.GREATER,
    value: undefined,
  },
  amazonOrderedUnits: {
    comparisonOperator: SearchFilterComparisonOperator.GREATER,
    value: undefined,
  },
  amazonConversionRate: {
    comparisonOperator: SearchFilterComparisonOperator.GREATER,
    value: undefined,
  },
};

export const SalesDataFilter = () => {
  const { t } = useTranslation(["books", "general"]);
  const { control, setValue } = useFormContext();
  const [openCalendar, setOpenCalendar] = useState<"from" | "to" | null>(null);
  const startDatePickerRef = useRef<HTMLDivElement>(null);
  const endDatePickerRef = useRef<HTMLDivElement>(null);
  const salesData =
    useWatch({ control, name: "filters.salesData" }) ?? defaultSalesDataFilter;
  const range = salesData.range ?? emptySalesDataRange;
  const today = startOfDay(new Date());
  const parsedRange = parseSalesDataRange(range);
  const validation = validateCompleteDateRange(range, { maxDate: today });
  const activePresetId = getActiveSalesDataPresetId(range, today);

  let validationMessage: string | undefined;
  if (validation.errorKey === "invalidDate") {
    validationMessage = t("books:filters.errors.invalidDate");
  }
  if (validation.errorKey === "fromAfterTo") {
    validationMessage = t("books:filters.errors.fromAfterTo");
  }
  if (validation.errorKey === "futureDate") {
    validationMessage = t("books:filters.errors.futureDate");
  }

  const presets = useMemo<{ id: SalesDataPresetId; label: string }[]>(
    () => [
      {
        id: "last-12-weeks",
        label: t("books:filters.salesData.presets.last12Weeks"),
      },
      {
        id: "current-year",
        label: t("books:filters.salesData.presets.currentYear"),
      },
      {
        id: "last-year",
        label: t("books:filters.salesData.presets.lastYear"),
      },
      {
        id: "lifetime",
        label: t("books:filters.salesData.presets.lifetime"),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (!openCalendar) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const activePicker =
        openCalendar === "from"
          ? startDatePickerRef.current
          : endDatePickerRef.current;
      if (
        activePicker &&
        event.target instanceof Node &&
        !activePicker.contains(event.target)
      ) {
        setOpenCalendar(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [openCalendar]);

  const updateRange = (nextRange: typeof range) => {
    setValue("filters.salesData.range", nextRange);
  };

  const handleDateTextChange = (key: "from" | "to", value: string) => {
    updateRange({ ...range, [key]: value });
  };

  const handlePresetClick = (presetId: SalesDataPresetId) => {
    if (activePresetId === presetId) {
      updateRange(emptySalesDataRange);
      return;
    }

    updateRange(buildSalesDataPresetRange(presetId, today));
  };

  const handleCalendarChange = (key: "from" | "to", date: Date | undefined) => {
    updateRange({
      ...range,
      [key]: date ? formatSalesDataInputDate(date) : "",
    });
    setOpenCalendar(null);
  };

  const handleDatePickerBlur = (event: FocusEvent<HTMLDivElement>): void => {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }

    setOpenCalendar(null);
  };

  const hasMetricValue = (metricKey: SalesDataMetricKey) =>
    hasValidSalesDataMetricValue(metricKey, salesData[metricKey]?.value);

  const clearMetric = (metricKey: SalesDataMetricKey) => {
    setValue(`filters.salesData.${metricKey}.value`, undefined);
  };

  return (
    <div className="px-6 space-y-5">
      <div className="space-y-2">
        <Text as="p" variant="label">
          {t("books:filters.salesData.dateRange")}
        </Text>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <Text as="span" variant="caption">
              {t("books:filters.salesData.startDate")}
            </Text>
            {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions lint/a11y/noStaticElementInteractions: the wrapper only tracks focus within it; the controls inside are interactive */}
            <div
              ref={startDatePickerRef}
              className="relative"
              onBlur={handleDatePickerBlur}
            >
              <DateInput
                aria-label={t("books:filters.salesData.startDate")}
                className="w-full [&>span]:px-2"
                inputClassName="h-10 text-sm"
                value={range.from}
                aria-invalid={!validation.isValid}
                aria-describedby={
                  validationMessage ? DATE_RANGE_ERROR_ID : undefined
                }
                onFocus={() => setOpenCalendar("from")}
                onChange={(value) => handleDateTextChange("from", value)}
              />
              {openCalendar === "from" && (
                <div className={cn(CALENDAR_OVERLAY_CLASS_NAME, "left-0")}>
                  <StyledCalendar
                    mode="single"
                    selected={parsedRange.from}
                    disabled={{ after: today }}
                    onSelect={(date) => handleCalendarChange("from", date)}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Text as="span" variant="caption">
              {t("books:filters.salesData.endDate")}
            </Text>
            {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions lint/a11y/noStaticElementInteractions: the wrapper only tracks focus within it; the controls inside are interactive */}
            <div
              ref={endDatePickerRef}
              className="relative"
              onBlur={handleDatePickerBlur}
            >
              <DateInput
                aria-label={t("books:filters.salesData.endDate")}
                className="w-full [&>span]:px-2"
                inputClassName="h-10 text-sm"
                value={range.to}
                aria-invalid={!validation.isValid}
                aria-describedby={
                  validationMessage ? DATE_RANGE_ERROR_ID : undefined
                }
                onFocus={() => setOpenCalendar("to")}
                onChange={(value) => handleDateTextChange("to", value)}
              />
              {openCalendar === "to" && (
                <div className={cn(CALENDAR_OVERLAY_CLASS_NAME, "right-0")}>
                  <StyledCalendar
                    mode="single"
                    selected={parsedRange.to}
                    disabled={{ after: today }}
                    onSelect={(date) => handleCalendarChange("to", date)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {validationMessage && (
          <Text id={DATE_RANGE_ERROR_ID} variant="errorSmall">
            {validationMessage}
          </Text>
        )}
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <Button
              key={preset.id}
              type="button"
              variant="tertiary"
              size="small"
              label={preset.label}
              className={cn(
                "h-9 rounded-full border-secondary-200 px-5 text-sm font-medium shadow-none",
                activePresetId === preset.id
                  ? "bg-secondary-50 text-secondary-900"
                  : "bg-white text-secondary-900 hover:bg-secondary-25",
              )}
              aria-pressed={activePresetId === preset.id}
              onClick={() => handlePresetClick(preset.id)}
            />
          ))}
        </div>
      </div>

      <div className="space-y-4 border-t border-secondary-200 pt-4">
        <SalesDataMetricRow
          metricKey="totalUnits"
          label={t("books:filters.salesData.totalUnits")}
          id="sales-data-total-units"
          hasValue={hasMetricValue("totalUnits")}
          onClear={() => clearMetric("totalUnits")}
        />
        <SalesDataMetricRow
          metricKey="amazonOrderedUnits"
          label={t("books:filters.salesData.amazonOrderedUnits")}
          tooltip={t("books:filters.salesData.amazonOrderedUnitsTooltip")}
          id="sales-data-amazon-ordered-units"
          hasValue={hasMetricValue("amazonOrderedUnits")}
          onClear={() => clearMetric("amazonOrderedUnits")}
        />
        <SalesDataMetricRow
          metricKey="amazonConversionRate"
          label={t("books:filters.salesData.amazonConversionRate")}
          tooltip={t("books:filters.salesData.amazonConversionRateTooltip")}
          id="sales-data-amazon-conversion-rate"
          hasValue={hasMetricValue("amazonConversionRate")}
          onClear={() => clearMetric("amazonConversionRate")}
          max={100}
          suffix="%"
        />
      </div>
    </div>
  );
};
